import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { AlertTriangle, ArrowRight, Check, Plus, Trash2 } from "lucide-react";

// Krémové pole bez rámečku — společné primitivum dialogu (funguje i v tmavém režimu).
const FIELD = "h-12 mt-1.5 bg-secondary border-0 rounded-[10px] px-4 text-[15px] focus-visible:ring-2 focus-visible:ring-brand";
const LABEL = "text-[15px] font-medium text-foreground";

const DONE_ST = ["Hotovo", "Schváleno"];
const shortDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d) ? null : `${d.getDate()}. ${d.getMonth() + 1}.`;
};

// Sdílený dialog „Nová fáze" / „Upravit fázi" — používá harmonogram zakázky i globální Plánování.
// Desktop = centrovaný modal, mobil = spodní sheet (viz design handoff Události harmonogramu).
export function PhaseFormDialog({
  open, onOpenChange, mode = "edit", projectName, onNavigateProject,
  form, setForm, responsibles = [], phases = [], taskStats, onSubmit, submitting,
  projects, onProjectChange, tasks = [], onOpenTask, onAddTask, onDelete,
}) {
  const isEdit = mode === "edit";
  const autoProg = taskStats && taskStats.total > 0;
  const set = (k, v) => setForm({ ...form, [k]: v });
  const depOptions = phases.filter((p) => p.id !== form.id);
  const doneCount = tasks.filter((t) => DONE_ST.includes(t.status)).length;
  const invalidRange = form.start_date && form.end_date && form.end_date < form.start_date;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="phase-form-dialog"
        className="max-w-[600px] max-h-[90vh] overflow-y-auto p-0 gap-0
          max-sm:top-auto max-sm:bottom-0 max-sm:translate-y-0 max-sm:max-h-[88vh]
          max-sm:rounded-b-none max-sm:rounded-t-[26px] max-sm:data-[state=open]:slide-in-from-bottom">
        <div className="p-6 sm:px-8 sm:pt-7 sm:pb-8">
          <DialogHeader className="text-left space-y-0 pr-8">
            <div className="flex items-center gap-2.5 flex-wrap">
              <span className="text-[11.5px] sm:text-[12.5px] font-bold uppercase tracking-[0.09em] text-muted-foreground">{projectName}</span>
              {onNavigateProject && (
                <button type="button" onClick={onNavigateProject} data-testid="phase-form-project-link"
                  className="text-[11.5px] sm:text-[12.5px] font-bold uppercase tracking-[0.09em] text-brand hover:underline flex items-center gap-1">
                  Harmonogram <ArrowRight size={12} />
                </button>
              )}
            </div>
            <DialogTitle className="text-[27px] sm:text-[34px] font-bold tracking-tight leading-[1.05] pt-1">
              {isEdit ? "Upravit fázi" : "Nová fáze"}
            </DialogTitle>
            <DialogDescription className="sr-only">Úprava fáze harmonogramu</DialogDescription>
          </DialogHeader>

          <div className="mt-5 space-y-5">
            {projects && (
              <div>
                <Label className={LABEL}>Zakázka <span className="text-destructive">*</span></Label>
                <Select value={form.projectId || ""} onValueChange={onProjectChange}>
                  <SelectTrigger className={FIELD} data-testid="phase-form-project"><SelectValue placeholder="Vyberte zakázku" /></SelectTrigger>
                  <SelectContent>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label className={LABEL}>Název</Label>
              <Input data-testid="phase-form-name" value={form.name || ""} onChange={(e) => set("name", e.target.value)}
                className={FIELD} autoFocus={!isEdit} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className={LABEL}>Od</Label>
                <Input type="date" data-testid="phase-form-start" value={form.start_date || ""}
                  onChange={(e) => set("start_date", e.target.value)} className={`${FIELD} w-full min-w-0`} />
              </div>
              <div>
                <Label className={LABEL}>Do</Label>
                <Input type="date" data-testid="phase-form-end" value={form.end_date || ""}
                  onChange={(e) => set("end_date", e.target.value)}
                  className={`${FIELD} w-full min-w-0 ${invalidRange ? "ring-2 ring-destructive" : ""}`} />
                {invalidRange && <p className="text-[13px] font-medium text-destructive mt-1" data-testid="phase-form-range-error">Konec musí být po začátku.</p>}
              </div>
            </div>

            <div>
              <Label className={LABEL}>Kdo na tom dělá <span className="text-destructive">*</span></Label>
              <div className="mt-1.5 flex flex-wrap gap-2 max-h-44 overflow-y-auto p-0.5" data-testid="phase-form-responsibles">
                {responsibles.map((u) => {
                  const on = (form.responsible_ids || []).includes(u.id);
                  return (
                    <button type="button" key={u.id} data-testid={`phase-form-person-${u.id}`}
                      onClick={() => set("responsible_ids", on
                        ? (form.responsible_ids || []).filter((x) => x !== u.id)
                        : [...(form.responsible_ids || []), u.id])}
                      className={`h-10 px-3.5 rounded-full text-[14px] font-semibold border transition-colors ${on
                        ? "bg-brand text-white border-brand"
                        : "bg-secondary border-transparent text-foreground hover:border-brand/40"}`}>
                      {u.name}{u.role === "SUBCONTRACTOR" ? " · subdodavatel" : ""}
                    </button>
                  );
                })}
                {responsibles.length === 0 && <span className="text-sm text-muted-foreground">Žádní členové týmu.</span>}
              </div>
              {!(form.responsible_ids || []).length && (
                <div className="text-xs text-amber-600 dark:text-amber-400 mt-1.5 flex items-center gap-1"><AlertTriangle size={12} /> Vyberte aspoň jednoho člověka</div>
              )}
            </div>

            <div>
              <Label className={LABEL}>Závisí na <span className="font-normal text-muted-foreground">— volitelné</span></Label>
              <Select value={form.depends_on || "none"} onValueChange={(v) => set("depends_on", v === "none" ? "" : v)}>
                <SelectTrigger className={FIELD} data-testid="phase-form-depends-on"><SelectValue placeholder="Žádná" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Žádná</SelectItem>
                  {depOptions.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Postup dokončení */}
          <div className="mt-6 pt-6 border-t border-border">
            <h3 className="text-[17px] font-semibold">Postup dokončení</h3>
            {autoProg ? (
              <div className="mt-3 text-sm bg-secondary rounded-[10px] p-3.5" data-testid="phase-form-auto-progress">
                <div className="font-semibold text-foreground">{Math.round((taskStats.done / taskStats.total) * 100)} % · počítáno z úkolů ({taskStats.done}/{taskStats.total})</div>
                <div className="text-muted-foreground text-xs mt-1">Tato fáze má úkoly, proto se % počítá automaticky a nelze ho nastavit ručně.</div>
              </div>
            ) : (
              <div className="mt-3 space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[17px]">Označit jako dokončeno</span>
                  <Switch checked={(form.progress || 0) >= 100} onCheckedChange={(c) => set("progress", c ? 100 : 0)} data-testid="phase-form-done-switch" />
                </div>
                <div>
                  <div className="flex justify-between items-baseline mb-2">
                    <span className="text-[15px] text-muted-foreground">Rozpracovanost</span>
                    <span className="text-[17px] font-bold tabular-nums">{form.progress || 0}&nbsp;%</span>
                  </div>
                  <Slider value={[form.progress || 0]} min={0} max={100} step={5} onValueChange={(v) => set("progress", v[0])} data-testid="phase-form-progress-slider" />
                </div>
              </div>
            )}
          </div>

          {/* Úkoly fáze — jen v editaci, když je k dispozici seznam */}
          {isEdit && (tasks.length > 0 || onAddTask) && (
            <div className="mt-6 pt-6 border-t border-border" data-testid="phase-form-tasks">
              <div className="flex items-center justify-between gap-3 mb-1">
                <h3 className="text-[17px] font-semibold">
                  Úkoly {tasks.length > 0 && <span className="font-normal text-muted-foreground text-[15px]">{doneCount} z {tasks.length}</span>}
                </h3>
                {onAddTask && (
                  <button type="button" onClick={onAddTask} data-testid="phase-form-add-task"
                    className="text-[13.5px] font-semibold uppercase tracking-wide text-brand hover:underline flex items-center gap-1">
                    <Plus size={14} /> Přidat úkol
                  </button>
                )}
              </div>
              {tasks.length === 0 ? (
                <p className="text-sm text-muted-foreground">Fáze zatím nemá žádné úkoly.</p>
              ) : (
                <div className="-mx-1">
                  {tasks.map((t) => {
                    const done = DONE_ST.includes(t.status);
                    const meta = [shortDate(t.deadline), (t.assignee_users || []).map((a) => a.name).join(", ")].filter(Boolean).join(" · ");
                    return (
                      <button key={t.id} type="button" onClick={() => onOpenTask && onOpenTask(t)}
                        data-testid={`phase-form-task-${t.id}`}
                        className="w-full flex items-center gap-3 px-1 py-2.5 rounded-lg text-left hover:bg-secondary/60 transition-colors">
                        <span className={`w-5 h-5 rounded-[5px] shrink-0 grid place-items-center ${done ? "bg-brand text-white" : "border-[1.5px] border-muted-foreground/40"}`}>
                          {done && <Check size={12} strokeWidth={3} />}
                        </span>
                        <span className={`flex-1 min-w-0 truncate text-[16px] ${done ? "text-muted-foreground line-through" : ""}`}>{t.title}</span>
                        {meta && <span className="text-[13.5px] text-muted-foreground shrink-0 hidden sm:block">{meta}</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Akce — desktop: Smazat vedle Uložit; mobil: Uložit nahoře, Smazat pod ním */}
          <div className="mt-7 flex flex-col-reverse sm:flex-row gap-3">
            {isEdit && onDelete && (
              <Button type="button" variant="ghost" onClick={onDelete} data-testid="phase-form-delete"
                className="h-12 sm:h-[52px] sm:px-6 font-semibold text-muted-foreground sm:bg-secondary hover:bg-destructive/10 hover:text-destructive">
                <Trash2 size={16} /> Smazat
              </Button>
            )}
            <Button onClick={onSubmit} disabled={submitting || invalidRange} data-testid="phase-form-save"
              className="flex-1 h-[52px] rounded-xl font-bold text-[17px] sm:text-[18px]">
              {isEdit ? "Uložit" : "Přidat fázi"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
